import type { QueuedOperation } from '@/shared/apiTypes';
import type { SyncState } from '@/shared/apiTypes';
import { getProjectIdFromNodeId } from '@/features/projects/utils/projectUtils';
import { transformOperationToBackend } from '@/shared/utils/fieldNameTransform';

export class PendingOperationProjectRoutingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PendingOperationProjectRoutingError';
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

export function isQueuedOperation(value: unknown): value is QueuedOperation {
  if (!isRecord(value)) return false;
  if (typeof value.id !== 'string' || value.id.length === 0) return false;
  if (!isRecord(value.operation)) return false;
  if (typeof value.operation.type !== 'string' || value.operation.type.length === 0) return false;
  if (typeof value.timestamp !== 'number' || Number.isNaN(value.timestamp)) return false;
  if (typeof value.retries !== 'number') return false;
  if (typeof value.maxRetries !== 'number') return false;
  return true;
}

export function assertQueuedOperations(pendingOperations: unknown[]): QueuedOperation[] {
  if (!Array.isArray(pendingOperations)) {
    throw new PendingOperationProjectRoutingError('Pending operations must be an array');
  }

  return pendingOperations.map((operation, index) => {
    if (!isQueuedOperation(operation)) {
      throw new PendingOperationProjectRoutingError(
        `Pending operation at index ${index} is not a QueuedOperation`,
      );
    }
    return operation;
  });
}

function explicitProjectId(candidates: Array<string | undefined>): string | undefined {
  const ids = [...new Set(candidates.filter((value): value is string => typeof value === 'string' && value.trim().length > 0))];
  if (ids.length > 1) {
    throw new PendingOperationProjectRoutingError('Pending operation has conflicting project identifiers');
  }
  return ids[0];
}

export function resolveQueuedOperationProjectId(operation: QueuedOperation): string {
  const nodeId =
    typeof operation.operation.nodeId === 'string' ? operation.operation.nodeId : undefined;
  const namespacedProjectId =
    nodeId && nodeId.includes(':') ? getProjectIdFromNodeId(nodeId) : undefined;

  const projectId = explicitProjectId([
    operation.projectId,
    namespacedProjectId,
  ]);

  if (!projectId) {
    throw new PendingOperationProjectRoutingError(
      'Pending operations are missing an explicit project id',
    );
  }

  return projectId;
}

export function resolvePendingOperationProjectId(
  operations: QueuedOperation[],
): string {
  if (operations.length === 0) {
    throw new PendingOperationProjectRoutingError('Cannot route an empty pending-operation batch');
  }
  const projectIds = operations.map((operation) => resolveQueuedOperationProjectId(operation));
  const uniqueProjectIds = [...new Set(projectIds)];
  if (uniqueProjectIds.length !== 1) {
    throw new PendingOperationProjectRoutingError(
      `Pending operations span multiple projects: ${uniqueProjectIds.join(', ')}`,
    );
  }
  return uniqueProjectIds[0];
}

export function buildPendingOperationBatchPayload(
  pendingOperations: unknown[],
  localVersion: number,
): {
  project_id: string;
  version: number;
  operations: Array<Record<string, unknown> & { id: string }>;
} {
  const operations = assertQueuedOperations(pendingOperations);
  const projectId = resolvePendingOperationProjectId(operations);

  return {
    project_id: projectId,
    version: localVersion,
    operations: operations.map((queued) => {
      const transformed: unknown = transformOperationToBackend(queued.operation);
      if (!isRecord(transformed)) throw new PendingOperationProjectRoutingError('Invalid operation payload');
      return {
        ...transformed,
        id: queued.id,
        project_id: projectId,
        ...(queued.operation.targetIndex !== undefined ? { target_index: queued.operation.targetIndex } : {}),
      };
    }),
  };
}

export function acknowledgedOperationsFromResponse(
  response: unknown,
  submitted: QueuedOperation[],
): { acknowledged: Set<QueuedOperation>; hasFailures: boolean } {
  const data = isRecord(response) && response.success === true && isRecord(response.data) ? response.data : null;
  const results = data?.results;
  // This endpoint returns one result in submission order, including failures.
  // HTTP 200 alone is not an acknowledgement, and it has no newVersion field.
  if (!Array.isArray(results) || results.length !== submitted.length ||
      results.some(result => !isRecord(result) || typeof result.success !== 'boolean')) {
    throw new PendingOperationProjectRoutingError('Missing or invalid batch acknowledgement');
  }
  const acknowledged = new Set(submitted.filter((_, index) => results[index].success === true));
  return { acknowledged, hasFailures: acknowledged.size !== submitted.length };
}

export function applyAcknowledgedBatchToSyncState(
  prev: SyncState,
  acknowledged: Set<QueuedOperation>,
  lastSyncTime: number,
  hasFailures = false,
): SyncState {
  // Preserve later replacements even when a queue reuses the same operation id.
  const pendingOperations = prev.pendingOperations.filter(operation => !acknowledged.has(operation));
  return {
    ...prev,
    pendingOperations,
    lastSyncTime,
    syncStatus: hasFailures ? 'conflict' : pendingOperations.length > 0 ? 'pending' : 'synced',
  };
}
